import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, FileText, ShieldCheck, AlertTriangle, Bell, Thermometer,
  ClipboardList, Boxes, Truck, Bug, Sparkles, Play, Upload, CalendarClock, FlaskConical,
} from "lucide-react";

const ORANGE = "#FF5625";
const DARK = "#211c18";
const CREAM = "#faf6f1";

// ── TIMING · ÚNICA FUENTE DE VERDAD ─────────────────────────────────────────
// Duración de cada escena EN SEGUNDOS. Cambia un `dur` para recuantizar esa
// escena; el auto-avance y la barra de progreso leen de aquí. Total ≈ 43 s.
// SPEED multiplica TODAS las duraciones a la vez: 1 = normal · <1 acelera · >1 ralentiza.
const SPEED = 1;
const SCENES = [
  { id: "intro", dur: 4.5 },
  { id: "ingesta", dur: 6.0 },
  { id: "mapeo", dur: 6.0 },
  { id: "huecos", dur: 5.5 },
  { id: "correccion", dur: 6.0 },
  { id: "readiness", dur: 5.5 },
  { id: "monitor", dur: 5.0 },
  { id: "cierre", dur: 4.5 },
];

// Duración total (ms) expuesta para el renderizador de vídeo del backend.
if (typeof window !== "undefined") {
  window.SHOWCASE_TOTAL_MS = SCENES.reduce((a, s) => a + s.dur, 0) * 1000 * SPEED;
}

// ── Marca ─────────────────────────────────────────────────────────────────
function Wordmark({ size = "text-[3vh]", logoUrl }) {
  if (logoUrl) return <img src={logoUrl} alt="AuditReady" className="h-[4.5vh] w-auto object-contain" />;
  return (
    <div className="flex items-center gap-[1.2vh]">
      <div className="grid place-items-center rounded-[1.1vh] shadow-lg" style={{ background: ORANGE, width: "4.6vh", height: "4.6vh" }}>
        <Check strokeWidth={3.5} color="#fff" style={{ width: "2.8vh", height: "2.8vh" }} />
      </div>
      <span className={`${size} font-black tracking-tight`} style={{ color: DARK }}>
        Audit<span style={{ color: ORANGE }}>Ready</span>
      </span>
    </div>
  );
}

function BurnedLogo({ logoUrl }) {
  return (
    <div className="absolute top-[3.5vh] left-[3.5vh] z-50 pointer-events-none" style={{ opacity: 0.85 }}>
      <Wordmark size="text-[2.6vh]" logoUrl={logoUrl} />
    </div>
  );
}

function Watermark() {
  const cols = Array.from({ length: 60 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden style={{ opacity: 0.04 }}>
      <div className="absolute -inset-1/4 flex flex-wrap gap-[6vh] rotate-[-24deg]">
        {cols.map((_, i) => (
          <span key={i} className="text-[3.2vh] font-black tracking-tight whitespace-nowrap" style={{ color: DARK }}>
            AuditReady
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Utilidades de animación ─────────────────────────────────────────────────
function useCountUp(target, duration = 1600, delay = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf, start;
    const t = setTimeout(() => {
      const step = (now) => {
        if (!start) start = now;
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(eased * target));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [target, duration, delay]);
  return val;
}

const easeCard = { type: "spring", stiffness: 120, damping: 18 };

function Panel({ children, className = "", style }) {
  return (
    <div className={`rounded-[2vh] bg-white shadow-[0_4vh_8vh_-3vh_rgba(33,28,24,0.25)] border border-black/[0.04] ${className}`} style={style}>
      {children}
    </div>
  );
}

// ── Escenas ───────────────────────────────────────────────────────────────
function Intro({ logoUrl }) {
  return (
    <div className="h-full w-full grid place-items-center text-center px-[8vw]">
      <div>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ ...easeCard, delay: 0.1 }} className="flex justify-center mb-[4vh]">
          <div className="scale-[2.4] origin-center"><Wordmark logoUrl={logoUrl} /></div>
        </motion.div>
        <motion.h1 initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5, duration: 0.7 }}
          className="text-[5vh] leading-[1.08] font-black tracking-tight max-w-[70vw] mx-auto" style={{ color: DARK }}>
          Preparación de auditoría <span style={{ color: ORANGE }}>IFS Food v8</span>, en tiempo real.
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-[3vh] text-[2.4vh] font-medium" style={{ color: `${DARK}99` }}>
          El copiloto que deja tu documentación de calidad lista para el auditor.
        </motion.p>
      </div>
    </div>
  );
}

const DOCS = [
  { n: "Manual APPCC", i: ClipboardList },
  { n: "Plan de control de alérgenos", i: FlaskConical },
  { n: "Registros de temperatura – cámara 3", i: Thermometer },
  { n: "Fichas técnicas de proveedor", i: Truck },
  { n: "Plan de trazabilidad", i: Boxes },
  { n: "Control de plagas", i: Bug },
];

function Ingesta() {
  const count = useCountUp(142, 2600, 300);
  return (
    <div className="h-full w-full grid grid-cols-2 items-center px-[7vw] gap-[5vw]">
      <div>
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="text-[2vh] font-bold uppercase tracking-[0.3em] mb-[2vh]" style={{ color: ORANGE }}>
          Ingesta
        </motion.div>
        <h2 className="text-[4.4vh] leading-[1.1] font-black tracking-tight" style={{ color: DARK }}>
          AuditReady lee toda vuestra<br />documentación de calidad.
        </h2>
        <div className="mt-[4vh] flex items-baseline gap-[1.5vh]">
          <span className="text-[9vh] font-black tabular-nums leading-none" style={{ color: ORANGE }}>{count}</span>
          <span className="text-[2.6vh] font-semibold" style={{ color: `${DARK}cc` }}>documentos analizados</span>
        </div>
      </div>
      <div className="relative h-[62vh]">
        {DOCS.map((d, i) => {
          const Icon = d.i;
          return (
            <motion.div key={d.n} initial={{ y: -120, opacity: 0, rotate: -3 }} animate={{ y: i * 9.2 + "vh", opacity: 1, rotate: 0 }}
              transition={{ ...easeCard, delay: 0.25 + i * 0.28 }} className="absolute left-0 right-[4vw]">
              <Panel className="px-[2.4vh] py-[2vh] flex items-center gap-[2vh]">
                <div className="grid place-items-center rounded-[1.2vh] shrink-0" style={{ background: `${ORANGE}1a`, width: "5.4vh", height: "5.4vh" }}>
                  <Icon color={ORANGE} style={{ width: "2.8vh", height: "2.8vh" }} />
                </div>
                <div className="min-w-0">
                  <div className="text-[2.3vh] font-bold truncate" style={{ color: DARK }}>{d.n}</div>
                  <div className="text-[1.7vh]" style={{ color: `${DARK}80` }}>PDF · analizado</div>
                </div>
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6 + i * 0.28, type: "spring", stiffness: 300 }}
                  className="ml-auto grid place-items-center rounded-full shrink-0" style={{ background: "#16a34a", width: "3.4vh", height: "3.4vh" }}>
                  <Check color="#fff" strokeWidth={3} style={{ width: "2vh", height: "2vh" }} />
                </motion.div>
              </Panel>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

const REQS = [
  "1. Responsabilidad de la dirección",
  "2. Sistema de gestión de la calidad",
  "3. Gestión de los recursos",
  "4. Proceso operativo",
  "5. Mediciones y análisis",
  "6. Food Defense",
];

function Mapeo() {
  return (
    <div className="h-full w-full grid grid-cols-2 items-center px-[7vw] gap-[5vw]">
      <div>
        <div className="text-[2vh] font-bold uppercase tracking-[0.3em] mb-[2vh]" style={{ color: ORANGE }}>Mapeo contra la norma</div>
        <h2 className="text-[4.4vh] leading-[1.1] font-black tracking-tight" style={{ color: DARK }}>
          Cruza cada documento con los<br />requisitos de la norma.
        </h2>
        <p className="mt-[3vh] text-[2.2vh] font-medium max-w-[34vw]" style={{ color: `${DARK}99` }}>
          IFS Food v8, capítulo a capítulo, con la evidencia que lo respalda.
        </p>
      </div>
      <Panel className="p-[3vh]">
        {REQS.map((r, i) => (
          <motion.div key={r} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.5 }}
            className="flex items-center gap-[2vh] py-[1.7vh] border-b last:border-0" style={{ borderColor: "#00000010" }}>
            <motion.div initial={{ scale: 0, background: "#e5e7eb" }} animate={{ scale: 1, background: "#16a34a" }} transition={{ delay: 0.55 + i * 0.5, type: "spring", stiffness: 300 }}
              className="grid place-items-center rounded-full shrink-0" style={{ width: "3.6vh", height: "3.6vh" }}>
              <Check color="#fff" strokeWidth={3} style={{ width: "2.1vh", height: "2.1vh" }} />
            </motion.div>
            <span className="text-[2.3vh] font-semibold" style={{ color: DARK }}>{r}</span>
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 + i * 0.5 }}
              className="ml-auto text-[1.6vh] font-semibold px-[1.4vh] py-[0.5vh] rounded-full" style={{ background: `${ORANGE}14`, color: ORANGE }}>
              {3 + i} docs
            </motion.span>
          </motion.div>
        ))}
      </Panel>
    </div>
  );
}

const HUECOS = [
  { t: "Plan de control de alérgenos desactualizado", s: "Versión 2023 · requiere revisión anual", sev: "rojo" },
  { t: "Registro de trazabilidad incompleto", s: "Falta el lote de salida en 2 expediciones", sev: "ambar" },
  { t: "Ficha técnica de proveedor caducada", s: "Proveedor X · caducó hace 41 días", sev: "rojo" },
];

function Huecos() {
  return (
    <div className="h-full w-full grid place-items-center px-[7vw]">
      <div className="w-full max-w-[72vw]">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-[4vh]">
          <div className="inline-flex items-center gap-[1.4vh] px-[2.4vh] py-[1.2vh] rounded-full" style={{ background: "#fef2f2", color: "#dc2626" }}>
            <AlertTriangle style={{ width: "2.6vh", height: "2.6vh" }} />
            <span className="text-[2.4vh] font-bold">3 no conformidades potenciales detectadas antes de la auditoría</span>
          </div>
        </motion.div>
        <div className="grid grid-cols-3 gap-[2.5vw]">
          {HUECOS.map((h, i) => {
            const rojo = h.sev === "rojo";
            const col = rojo ? "#dc2626" : "#d97706";
            const bg = rojo ? "#fef2f2" : "#fffbeb";
            return (
              <motion.div key={h.t} initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ ...easeCard, delay: 0.3 + i * 0.45 }}>
                <Panel className="p-[2.8vh] h-full" style={{ borderTop: `0.7vh solid ${col}` }}>
                  <div className="grid place-items-center rounded-[1.4vh] mb-[2vh]" style={{ background: bg, width: "6vh", height: "6vh" }}>
                    <AlertTriangle color={col} style={{ width: "3.2vh", height: "3.2vh" }} />
                  </div>
                  <div className="text-[2.3vh] font-black leading-tight" style={{ color: DARK }}>{h.t}</div>
                  <div className="mt-[1.4vh] text-[1.9vh]" style={{ color: `${DARK}99` }}>{h.s}</div>
                  <div className="mt-[2vh] text-[1.6vh] font-bold uppercase tracking-wide" style={{ color: col }}>
                    {rojo ? "Crítico" : "Con reservas"}
                  </div>
                </Panel>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Typewriter({ text, delay = 0, speed = 26 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let id; const t = setTimeout(() => { id = setInterval(() => setN((v) => (v < text.length ? v + 1 : v)), speed); }, delay);
    return () => { clearTimeout(t); clearInterval(id); };
  }, [text, delay, speed]);
  return <span>{text.slice(0, n)}<span className="inline-block w-[0.2vh] h-[2vh] align-middle ml-[0.2vh] animate-pulse" style={{ background: ORANGE }} /></span>;
}

function Correccion() {
  const fixed = [0, 1, 2];
  return (
    <div className="h-full w-full grid grid-cols-2 items-center px-[7vw] gap-[4vw]">
      <div>
        <div className="text-[2vh] font-bold uppercase tracking-[0.3em] mb-[2vh]" style={{ color: ORANGE }}>Generación y corrección</div>
        <h2 className="text-[4.4vh] leading-[1.1] font-black tracking-tight" style={{ color: DARK }}>
          Genera y actualiza la<br />documentación que falta.
        </h2>
        <div className="mt-[4vh] space-y-[1.8vh]">
          {HUECOS.map((h, i) => (
            <motion.div key={h.t} className="flex items-center gap-[1.8vh]">
              <motion.div initial={{ background: "#dc2626", scale: 1 }} animate={{ background: "#16a34a", scale: [1, 1.25, 1] }} transition={{ delay: 1.4 + i * 0.7, duration: 0.5 }}
                className="grid place-items-center rounded-full shrink-0" style={{ width: "3.6vh", height: "3.6vh" }}>
                <Check color="#fff" strokeWidth={3} style={{ width: "2.1vh", height: "2.1vh" }} />
              </motion.div>
              <span className="text-[2.1vh] font-semibold" style={{ color: DARK }}>{h.t}</span>
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 + i * 0.7 }} className="ml-auto text-[1.7vh] font-bold" style={{ color: "#16a34a" }}>
                Resuelto
              </motion.span>
            </motion.div>
          ))}
        </div>
      </div>
      <Panel className="p-[3.5vh] h-[60vh] flex flex-col">
        <div className="flex items-center gap-[1.4vh] mb-[2.5vh]">
          <Sparkles color={ORANGE} style={{ width: "2.8vh", height: "2.8vh" }} />
          <span className="text-[2.1vh] font-black" style={{ color: DARK }}>Plan de control de alérgenos — v2026</span>
        </div>
        <div className="text-[2vh] leading-[1.8] font-medium" style={{ color: `${DARK}cc` }}>
          <Typewriter delay={300} text="1. Identificación de alérgenos presentes en la planta y en materias primas. 2. Evaluación de riesgo de contaminación cruzada por línea. 3. Medidas de control: segregación, limpieza validada y verificación por ATP. 4. Etiquetado y comunicación al cliente. 5. Revisión anual y ante cambios de producto." />
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 4.4 }} className="mt-auto flex items-center gap-[1.2vh] text-[1.8vh] font-bold" style={{ color: "#16a34a" }}>
          <Check strokeWidth={3} style={{ width: "2.2vh", height: "2.2vh" }} /> Documento actualizado y archivado
        </motion.div>
      </Panel>
    </div>
  );
}

function Gauge() {
  const pct = useCountUp(96, 2400, 400);
  const R = 120, C = 2 * Math.PI * R;
  return (
    <div className="h-full w-full grid place-items-center px-[7vw]">
      <div className="text-center">
        <div className="text-[2vh] font-bold uppercase tracking-[0.3em] mb-[3vh]" style={{ color: ORANGE }}>IFS Readiness</div>
        <div className="relative inline-grid place-items-center" style={{ width: "42vh", height: "42vh" }}>
          <svg viewBox="0 0 280 280" className="w-full h-full -rotate-90">
            <circle cx="140" cy="140" r={R} fill="none" stroke="#00000010" strokeWidth="20" />
            <motion.circle cx="140" cy="140" r={R} fill="none" stroke={ORANGE} strokeWidth="20" strokeLinecap="round"
              strokeDasharray={C} initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - 0.96) }} transition={{ duration: 2.4, ease: "easeOut", delay: 0.4 }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <div className="text-[13vh] font-black leading-none tabular-nums" style={{ color: DARK }}>{pct}<span className="text-[6vh]">%</span></div>
              <div className="text-[2vh] font-semibold" style={{ color: `${DARK}80` }}>listo para auditoría</div>
            </div>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 20, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 2.6, ...easeCard }}
          className="mt-[4vh] inline-flex items-center gap-[1.6vh] px-[3vh] py-[1.6vh] rounded-full" style={{ background: "#16a34a" }}>
          <ShieldCheck color="#fff" style={{ width: "3vh", height: "3vh" }} />
          <span className="text-[2.6vh] font-black text-white">Auditoría lista</span>
        </motion.div>
        <p className="mt-[3vh] text-[2.2vh] font-medium" style={{ color: `${DARK}99` }}>De un vistazo, cuánto os falta para estar listos.</p>
      </div>
    </div>
  );
}

function Monitor() {
  return (
    <div className="h-full w-full grid grid-cols-2 items-center px-[7vw] gap-[5vw]">
      <div>
        <div className="text-[2vh] font-bold uppercase tracking-[0.3em] mb-[2vh]" style={{ color: ORANGE }}>Monitorización continua</div>
        <h2 className="text-[4.4vh] leading-[1.1] font-black tracking-tight" style={{ color: DARK }}>
          AuditReady vigila 24/7<br />y avisa antes de que algo caduque.
        </h2>
        <p className="mt-[3vh] text-[2.2vh] font-medium max-w-[34vw]" style={{ color: `${DARK}99` }}>
          Sin sustos en la próxima visita del auditor.
        </p>
      </div>
      <div className="space-y-[2.5vh]">
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3, ...easeCard }}>
          <Panel className="p-[3vh] flex items-center gap-[2.4vh]" style={{ borderLeft: `0.7vh solid ${ORANGE}` }}>
            <div className="grid place-items-center rounded-[1.4vh]" style={{ background: `${ORANGE}1a`, width: "6.4vh", height: "6.4vh" }}>
              <CalendarClock color={ORANGE} style={{ width: "3.4vh", height: "3.4vh" }} />
            </div>
            <div>
              <div className="text-[2.4vh] font-black" style={{ color: DARK }}>Ficha de proveedor caduca en 12 días</div>
              <div className="text-[1.9vh]" style={{ color: `${DARK}99` }}>Proveedor Láctea del Norte · te avisamos con antelación</div>
            </div>
          </Panel>
        </motion.div>
        {[["Simulacro de retirada", "programado para el 14/07"], ["Revisión del Manual APPCC", "al día · próxima en 5 meses"]].map((a, i) => (
          <motion.div key={a[0]} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.3, ...easeCard }}>
            <Panel className="p-[2.6vh] flex items-center gap-[2vh]">
              <div className="grid place-items-center rounded-full" style={{ background: "#16a34a", width: "4.2vh", height: "4.2vh" }}>
                <Bell color="#fff" style={{ width: "2.3vh", height: "2.3vh" }} />
              </div>
              <div>
                <div className="text-[2.1vh] font-bold" style={{ color: DARK }}>{a[0]}</div>
                <div className="text-[1.8vh]" style={{ color: `${DARK}80` }}>{a[1]}</div>
              </div>
            </Panel>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function Cierre({ logoUrl }) {
  return (
    <div className="h-full w-full grid place-items-center text-center px-[8vw]">
      <div>
        <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={easeCard} className="flex justify-center mb-[4vh]">
          <div className="scale-[2.6] origin-center"><Wordmark logoUrl={logoUrl} /></div>
        </motion.div>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}
          className="text-[5vh] font-black tracking-tight" style={{ color: DARK }}>
          Siempre listo para la <span style={{ color: ORANGE }}>auditoría</span>.
        </motion.h2>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="mt-[3vh] text-[2.1vh] font-semibold tracking-wide" style={{ color: `${DARK}80` }}>
          por Agentik · VRTC
        </motion.div>
      </div>
    </div>
  );
}

function SceneRouter({ id, logoUrl }) {
  switch (id) {
    case "intro": return <Intro logoUrl={logoUrl} />;
    case "ingesta": return <Ingesta />;
    case "mapeo": return <Mapeo />;
    case "huecos": return <Huecos />;
    case "correccion": return <Correccion />;
    case "readiness": return <Gauge />;
    case "monitor": return <Monitor />;
    case "cierre": return <Cierre logoUrl={logoUrl} />;
    default: return null;
  }
}

export default function Showcase() {
  // Modo grabación: /showcase?record=1 → sin controles, autoplay y congela el frame final.
  const record = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("record") === "1";
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [prog, setProg] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const idxRef = useRef(0);
  const elapsedRef = useRef(0);
  const pausedRef = useRef(false);
  const frozenRef = useRef(false);
  useEffect(() => { idxRef.current = idx; }, [idx]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    let raf, last = performance.now();
    const tick = (now) => {
      const dt = now - last; last = now;
      if (!pausedRef.current && !frozenRef.current) {
        elapsedRef.current += dt;
        const dur = SCENES[idxRef.current].dur * 1000 * SPEED;
        setProg(Math.min(1, elapsedRef.current / dur));
        if (elapsedRef.current >= dur) {
          const isLast = idxRef.current === SCENES.length - 1;
          if (record && isLast) {
            frozenRef.current = true; setProg(1); // congela el frame final para cortar limpio
          } else {
            elapsedRef.current = 0; setProg(0); setIdx((i) => (i + 1) % SCENES.length);
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [record]);

  const replay = useCallback(() => { frozenRef.current = false; elapsedRef.current = 0; setProg(0); setIdx(0); setPaused(false); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") { e.preventDefault(); setPaused((p) => !p); }
      if (e.code === "KeyR") { e.preventDefault(); replay(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [replay]);

  const onLogo = (e) => { const f = e.target.files?.[0]; if (f) setLogoUrl(URL.createObjectURL(f)); };

  return (
    <div className="fixed inset-0 grid place-items-center overflow-hidden" style={{ background: DARK }}>
      {/* Escenario 16:9 */}
      <div className="relative overflow-hidden" style={{ width: "min(100vw, 177.78vh)", height: "min(56.25vw, 100vh)", background: CREAM }} data-testid="showcase-stage">
        <Watermark />
        <BurnedLogo logoUrl={logoUrl} />

        {/* Barra de progreso segmentada */}
        <div className="absolute top-0 left-0 right-0 z-40 flex gap-[0.4vh] px-[1vh] pt-[1vh]">
          {SCENES.map((s, i) => (
            <div key={s.id} className="flex-1 h-[0.6vh] rounded-full overflow-hidden" style={{ background: "#00000012" }}>
              <div className="h-full rounded-full" style={{ background: ORANGE, width: i < idx ? "100%" : i === idx ? `${prog * 100}%` : "0%", transition: i === idx ? "none" : "width 0.2s" }} />
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={SCENES[idx].id} initial={{ opacity: 0, scale: 1.015 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.99 }}
            transition={{ duration: 0.6, ease: "easeInOut" }} className="absolute inset-0" data-testid={`scene-${SCENES[idx].id}`}>
            <SceneRouter id={SCENES[idx].id} logoUrl={logoUrl} />
          </motion.div>
        </AnimatePresence>

        {/* Controles discretos — ocultos por completo en modo grabación (?record=1) */}
        {!record && <div className="absolute bottom-[2.5vh] right-[3vh] z-50 flex items-center gap-[1.5vh] opacity-40 hover:opacity-100 transition-opacity">
          <label className="cursor-pointer flex items-center gap-[0.8vh] text-[1.5vh] font-semibold px-[1.6vh] py-[1vh] rounded-full bg-white shadow" style={{ color: DARK }} title="Subir logo (PNG/SVG)">
            <Upload style={{ width: "1.8vh", height: "1.8vh" }} /> Logo
            <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="showcase-logo-input" />
          </label>
          <button onClick={replay} data-testid="showcase-replay" className="flex items-center gap-[0.8vh] text-[1.5vh] font-semibold px-[1.6vh] py-[1vh] rounded-full bg-white shadow" style={{ color: DARK }}>
            <Play style={{ width: "1.8vh", height: "1.8vh" }} /> Reproducir de nuevo
          </button>
        </div>}
        {!record && paused && (
          <div className="absolute bottom-[2.5vh] left-[3vh] z-50 text-[1.5vh] font-bold px-[1.6vh] py-[1vh] rounded-full" style={{ background: DARK, color: "#fff" }} data-testid="showcase-paused">
            En pausa · pulsa espacio
          </div>
        )}
      </div>
    </div>
  );
}
